/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import SurfaceColor from "App/geometry/material/surface_color";
import RenderOption from "App/geometry/material/render_option";
import PointProperties from "App/geometry/material/point_properties";
import LineProperties from "App/geometry/material/line_properties";
import {Texture, EDataTypeType} from "App/geometry/material/texture";
import MaterialLibrary from "App/geometry/material/material_library";
import {FLOAT_PRECISION} from "App/math";
import Description from "App/model/description";

export default
class Appearance {
    public pointProperties: PointProperties = null;
    public lineProperties: LineProperties = null;
    public renderOption: RenderOption = null;
    public texture: Texture = null;
    public surfaceColor: SurfaceColor = null;

    private _materialLibrary: MaterialLibrary = null;

    constructor(aMaterialLibrary: MaterialLibrary) {
        this._materialLibrary = aMaterialLibrary;

        // init materials
        let members = MaterialLibrary.getMaterialMembers();
        for (let member of members) {
            this[member] = null;
        }
    }

    public refMaterials() {
        let material = null;
        let members = MaterialLibrary.getMaterialMembers();
        for (let member of members) {
            material = this[member];
            if (material) {
                material.ref();
            }
        }
    }

    public unrefMaterials() {
        let material;
        let members = MaterialLibrary.getMaterialMembers();
        let categories = MaterialLibrary.getCategories();
        for (let i = 0; i < members.length; i++) {
            material = this[members[i]];
            if (material) {
                material.unref();
                if (material.getRefCount() === 0) {
                    this._materialLibrary.removeObject(
                        material,
                        categories[i]
                    );
                }
            }
        }
    }

    public hasTexture(aAppearanceProperties: any): boolean {
        // test if there is some texture compression format defined in appearance
        let hasTexture = false;
        for (let key in EDataTypeType) {
            if (aAppearanceProperties) {
                hasTexture = hasTexture || aAppearanceProperties.hasOwnProperty(key);
            } else {
                hasTexture = hasTexture || (this.texture && this.texture.hasOwnProperty(key));
            }

            if (hasTexture) {
                return true;
            }
        }

        return hasTexture;
    }

    public isOpaque(): boolean {
        return this.surfaceColor && Math.abs(this.surfaceColor.transparency) < FLOAT_PRECISION;
    }

    public hasAlphaTransparency(): boolean {
        return this.texture && this.texture.getAlpha();
    }

    public parse(aModelDescription: Description, aAppearanceProperties: any) {
        if (aAppearanceProperties === undefined) {
            aAppearanceProperties = {};
        }

        // here we look whether the aAppearanceProperties are defined in place for the input object or is just reference to material library
        // when it is defined in place, then we add it to the material library and transform the appearance to material library reference
        if (aAppearanceProperties.hasOwnProperty("materialRefs")) {
            let materialRefs = aAppearanceProperties.materialRefs;

            let categories = MaterialLibrary.getCategories();
            let members = MaterialLibrary.getMaterialMembers();
            for (let i = 0; i < categories.length; i++) {

                let uid = this._materialLibrary.getUidFromParsedId(
                    categories[i],
                    materialRefs[MaterialLibrary.getPositionInMaterialRefArray(categories[i])]
                );

                this[members[i]] = this._materialLibrary.getObjectByParsedID(uid, categories[i]);

                if (categories[i] === "surfaceColors" && this[members[i]] == null) {
                    // we need some default surface color all the time
                    this[members[i]] = this._materialLibrary._parseSurfaceColor(aModelDescription, undefined);
                }
                else if (categories[i] === "renderOptions" && this[members[i]] == null) {
                    // we need some default render option all the time
                    this[members[i]] = this._materialLibrary._parseRenderOption(aModelDescription, undefined);
                }
            }
        } else {
            // in place appearance
            let matLibObject = this._materialLibrary._parseSurfaceColor(aModelDescription, aAppearanceProperties.material);
            if (matLibObject) {
                this[SurfaceColor.TypeName] = matLibObject;
            }

            matLibObject = this._materialLibrary._parseRenderOption(aModelDescription, aAppearanceProperties.renderOption);
            if (matLibObject) {
                this[RenderOption.TypeName] = matLibObject;
            }

            matLibObject = this._materialLibrary._parsePointProperties(aModelDescription, aAppearanceProperties.pointProperties);
            if (matLibObject) {
                this[PointProperties.TypeName] = matLibObject;
            }

            matLibObject = this._materialLibrary._parseLineProperties(aModelDescription, aAppearanceProperties.lineProperties);
            if (matLibObject) {
                this[LineProperties.TypeName] = matLibObject;
            }

            if (this.hasTexture(aAppearanceProperties)) {
                let textureJson = {};
                for (let key in EDataTypeType) {
                    if (aAppearanceProperties.hasOwnProperty(key)) {
                        textureJson[key] = aAppearanceProperties[key];
                    }
                }
                matLibObject = this._materialLibrary._parseTexture(aModelDescription, textureJson);
                if (matLibObject) {
                    this[Texture.TypeName] = matLibObject;
                }
            }
        }
    }

    public getJSONRepresentation(): any {
        let materials = {};
        let members = MaterialLibrary.getMaterialMembers();
        for (let member of members) {
            let material = this[member];
            if (material) {
                if (member === "texture") { // exception for texture a we need to store the imageTexture etc. instead of texture { imageTexture ...}
                    let textures = material.getJSONRepresentation();
                    for (let key in textures) {
                        materials[key] = textures[key];
                    }
                } else {
                    materials[member] = material.getJSONRepresentation();
                }
            }
        }

        return materials;

        // TODO: export material library when loading form it will be available
    }
}
